if (typeof CurrencyConverter === 'undefined') {

const CurrencyConverter = {

  rates: {
    USD: 1,
    GBP: 0.75,
    BRL: 5.44,
    AUD: 1.42,
    SGD: 1.28,
    MXN: 18.78,
    EUR: 0.90,
    JPY: 157.80,
    SAR: 3.76,
    CNY: 6.66,
    RUB: 84.73,
  },

  symbols: {
    USD: '$',
    GBP: '£',
    BRL: '$',
    AUD: '$',
    SGD: '$',
    MXN: '$',
    EUR: '€',
    JPY: '¥',
    SAR: 'ر.س',
    CNY: '¥',
    RUB: '₽',
  },

  names: {
    USD: 'US Dollar',
    GBP: 'British Pound',
    BRL: 'Brazilian Real',
    AUD: 'Australian Dollar',
    SGD: 'Singapore Dollar',
    MXN: 'Mexican Peso',
    EUR: 'Euro',
    JPY: 'Japanese Yen',
    SAR: 'Saudi Arabian Riyal',
    CNY: 'Chinese Yuan',
    RUB: 'Russian Ruble',
  },

  currencyToCountry: {
    USD: { iso: 'us', label: 'US' },
    GBP: { iso: 'gb', label: 'GB' },
    BRL: { iso: 'br', label: 'BR' },
    AUD: { iso: 'au', label: 'AU' },
    SGD: { iso: 'sg', label: 'SG' },
    MXN: { iso: 'mx', label: 'MX' },
    EUR: { iso: 'eu', label: 'EU' },
    JPY: { iso: 'jp', label: 'JP' },
    SAR: { iso: 'sa', label: 'SA' },
    CNY: { iso: 'cn', label: 'CN' },
    RUB: { iso: 'ru', label: 'RU' },
  },

  noDecimals: ['JPY'],

  skipCountries: ['MY'],

  // Containers whose prices omit the currency code (USD only — a converted
  // price needs the code to stay unambiguous).
  hideCodeSelectors: '.wt-product__upsell__info, [data-hide-currency-code]',

  countryToCurrency: {
    US: 'USD',
    GB: 'GBP',
    BR: 'BRL',
    AU: 'AUD',
    SG: 'SGD',
    MX: 'MXN',
    DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR',
    NL: 'EUR', BE: 'EUR', AT: 'EUR', PT: 'EUR',
    IE: 'EUR', GR: 'EUR', FI: 'EUR', SK: 'EUR',
    JP: 'JPY',
    SA: 'SAR',
    CN: 'CNY',
    RU: 'RUB',
  },

  ipApis: [
    { url: 'https://ipapi.co/json/',                  getCode: (d) => d.country_code },
    { url: 'https://ip-api.com/json/',                getCode: (d) => d.countryCode  },
    { url: 'https://ipinfo.io/json?token=YOUR_TOKEN', getCode: (d) => d.country      },
  ],

  currentCurrency: 'USD',
  storageKey: 'shopify_currency_pref',
  cacheKey: 'visitor_currency_detected',
  _observer: null,
  _converting: false, // ✅ flicker guard flag

  // ─── INIT ────────────────────────────────────────────────
  async init() {
    const saved = localStorage.getItem(this.storageKey);
    if (saved && this.rates[saved]) {
      this.currentCurrency = saved;
      this._boot();
      return;
    }
    const cached = localStorage.getItem(this.cacheKey);
    if (cached && this.rates[cached]) {
      this.currentCurrency = cached;
      this._boot();
      return;
    }
    await this.detectFromIP();
    this._boot();
  },

  _boot() {
    this.buildDrawerList();
    this.buildDropdown();
    this.updateDrawerHeader();
    this.convertAllPrices();
    this.observeDynamicPrices();
  },

  // ─── IP DETECTION ────────────────────────────────────────
  async detectFromIP() {
    for (const api of this.ipApis) {
      try {
        const res = await fetch(api.url, { cache: 'force-cache' });
        if (!res.ok) throw new Error('Non-OK');
        const data = await res.json();
        if (data.status && data.status === 'fail') throw new Error('API fail');
        const countryCode = api.getCode(data);

        if (this.skipCountries.includes(countryCode)) {
          localStorage.setItem(this.cacheKey, 'USD');
          return;
        }

        const detected = this.countryToCurrency[countryCode];
        if (detected && this.rates[detected]) {
          this.currentCurrency = detected;
          localStorage.setItem(this.cacheKey, detected);
          return;
        }
      } catch (e) {
        continue;
      }
    }
  },

  // ─── FORMAT PRICE ────────────────────────────────────────
  formatPrice(amount, showCode = true) {
    const rate      = this.rates[this.currentCurrency] || 1;
    const symbol    = this.symbols[this.currentCurrency] || '$';
    const converted = amount * rate;
    const noDecimal = this.noDecimals.includes(this.currentCurrency);
    const rounded   = noDecimal
      ? Math.round(converted)
      : Math.round(converted * 100) / 100;
    const formatted = rounded.toLocaleString('en-US', {
      minimumFractionDigits: noDecimal ? 0 : 2,
      maximumFractionDigits: noDecimal ? 0 : 2,
    });
    return showCode
      ? `${symbol}${formatted} ${this.currentCurrency}`
      : `${symbol}${formatted}`;
  },

  // ─── CONVERT ALL .money SPANS ────────────────────────────
  convertAllPrices() {
    // ✅ Tell observer to ignore our own DOM writes
    this._converting = true;

    document.querySelectorAll('.money').forEach(el => {
      let baseUSD = el.getAttribute('data-original-usd');
      if (!baseUSD) {
        const raw = el.textContent.replace(/[^0-9.]/g, '');
        baseUSD = parseFloat(raw);
        if (isNaN(baseUSD)) return;
        el.setAttribute('data-original-usd', baseUSD);
      }
      const showCode = this.currentCurrency !== 'USD' || !el.closest(this.hideCodeSelectors);
      el.textContent = this.formatPrice(parseFloat(baseUSD), showCode);
    });

    const select = document.getElementById('currency-select');
    if (select) select.value = this.currentCurrency;

    this.updateDrawerHeader();

    // ✅ Release flag after DOM settles
    setTimeout(() => { this._converting = false; }, 300);
  },

  // ─── UPDATE HEADER/FOOTER BUTTONS + DRAWER ───────────────
  updateDrawerHeader() {
    const nameEl = document.getElementById('drawer-current-currency-name');
    const codeEl = document.getElementById('drawer-current-currency-code');
    if (nameEl) nameEl.textContent = this.names[this.currentCurrency] || this.currentCurrency;
    if (codeEl) codeEl.textContent = `${this.currentCurrency} ${this.symbols[this.currentCurrency].trim()}`;

    // ✅ Update flag + label on header/footer buttons
    const country = this.currencyToCountry[this.currentCurrency];
    if (country) {
      document.querySelectorAll('.currency-flag-display').forEach(el => {
        el.src = `https://flagcdn.com/w40/${country.iso}.png`;
        el.alt = country.label;
      });
      document.querySelectorAll('.currency-code-display').forEach(el => {
        /*el.textContent = `${country.label} /${this.currentCurrency}`;*/
        el.textContent = `${this.currentCurrency}`;
      });
    }

    // ✅ Clear all active states first — then set only current
    document.querySelectorAll('.currency-selector__trigger').forEach(el => {
      el.classList.remove('currency-selector__trigger--active');
      el.style.pointerEvents = 'auto';
      el.style.opacity = '1';
    });
    document.querySelectorAll(`.currency-selector__trigger[data-value="${this.currentCurrency}"]`).forEach(el => {
      el.classList.add('currency-selector__trigger--active');
      el.style.pointerEvents = 'none';
      el.style.opacity = '0.5';
    });
  },

  // ─── BUILD DRAWER LIST ───────────────────────────────────
 buildDrawerList() {
  const list = document.getElementById('drawer-currency-list');
  if (!list) return;

  // ✅ Clear existing items first to prevent duplicates
  list.innerHTML = '';

  const fragment = document.createDocumentFragment();

  Object.entries(this.rates).forEach(([code]) => {
    const isActive = code === this.currentCurrency;
    const country  = this.currencyToCountry[code];

    const li = document.createElement('li');
    li.className = 'country-selector__item';
    li.setAttribute('tabindex', '-1');
    li.setAttribute('data-filter-name', `${this.names[code]} ${code}`);

    const a = document.createElement('a');
    a.className = `country-selector__trigger${isActive ? ' currency-selector__trigger--active' : ''}`;
    a.href = '#';
    a.setAttribute('data-value', code);
    if (isActive) {
      a.style.pointerEvents = 'none';
      a.style.opacity = '0.5';
    }

    const flag = document.createElement('img');
    flag.src    = `https://flagcdn.com/w40/${country ? country.iso : 'us'}.png`;
    flag.alt    = code;
    flag.width  = 20;
    flag.height = 15;
    flag.style.cssText = 'border-radius:3px; margin-right:8px; vertical-align:middle; flex-shrink:0;';

    const name = document.createElement('span');
    name.className = 'country-selector__item__name';
    name.textContent = this.names[code] || code;

    const currency = document.createElement('span');
    currency.className = 'country-selector__item__currency';
    currency.textContent = code;

    a.append(flag, name, currency);
    li.appendChild(a);
    fragment.appendChild(li);

    a.addEventListener('click', (e) => {
  e.preventDefault();
  this.setCurrency(code);
  // ✅ Auto close using the custom element method
  const drawerEl = document.querySelector('localization-drawer');
  if (drawerEl && drawerEl.isOpen) {
    drawerEl.toggleDrawerClasses();
  }
});
  });

  list.appendChild(fragment);
},

  // ─── SET CURRENCY ────────────────────────────────────────
 setCurrency(code) {
  if (!this.rates[code] || code === this.currentCurrency) return;
  this.currentCurrency = code;
  localStorage.setItem(this.storageKey, code);
  this.convertAllPrices();
  // ✅ Rebuild list so previous active item is restored and new one is dimmed
  this.buildDrawerList();
  
},

  // ─── BUILD HEADER DROPDOWN (optional) ────────────────────
  buildDropdown() {
    const container = document.getElementById('currency-picker');
    if (!container) return;
    const wrapper = document.createElement('div');
    wrapper.className = 'currency-picker__wrapper';
    const select = document.createElement('select');
    select.id = 'currency-select';
    select.className = 'currency-picker__select';
    select.setAttribute('aria-label', 'Select currency');
    Object.entries(this.rates).forEach(([code]) => {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = `${this.symbols[code].trim()} ${code}`;
      if (code === this.currentCurrency) opt.selected = true;
      select.appendChild(opt);
    });
    select.addEventListener('change', (e) => this.setCurrency(e.target.value));
    wrapper.appendChild(select);
    container.appendChild(wrapper);
  },

  // ─── WATCH FOR AJAX UPDATES ──────────────────────────────
  observeDynamicPrices() {
    let debounceTimer;
    this._observer = new MutationObserver((mutations) => {
      // ✅ Skip if we are causing the mutations ourselves
      if (this._converting) return;

      // ✅ Only re-convert if new .money elements were actually added
      const hasNewPrices = mutations.some(m =>
        Array.from(m.addedNodes).some(node =>
          node.nodeType === 1 && (
            node.classList?.contains('money') ||
            node.querySelector?.('.money')
          )
        )
      );

      if (!hasNewPrices) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => this.convertAllPrices(), 300);
    });

    this._observer.observe(document.body, { childList: true, subtree: true });
  },

};

document.addEventListener('DOMContentLoaded', () => CurrencyConverter.init());

} // end guard